import { useState, type FormEvent, type ReactNode } from 'react';
import { account, type Role, type UcetPrehlad } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { Badge, Button, Card, Field, Notice, SectionTitle } from '../components/ui.js';
import { IconPlus, IconTrash } from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data } from './common.js';

/*
 * Zmena vlastneho hesla a sprava uctov.
 *
 * Su na obrazovke Systém, lebo tam clovek chodi za nastavenim, nie za
 * prevadzkou. Zmena hesla je pre kazdeho; zoznam uctov vidi len spravca
 * a rozhoduje o tom server, nie skryte tlacidlo.
 */

export function ZmenaHesla(): ReactNode {
  const [sucasne, setSucasne] = useState('');
  const [nove, setNove] = useState('');
  const [znova, setZnova] = useState('');
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [hotovo, setHotovo] = useState(false);
  const [prebieha, setPrebieha] = useState(false);

  const odosli = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    setHotovo(false);

    if (nove !== znova) {
      setChyba('Nové heslá sa nezhodujú.');
      return;
    }
    setPrebieha(true);
    void account
      .changePassword(sucasne, nove)
      .then(() => {
        setSucasne('');
        setNove('');
        setZnova('');
        setHotovo(true);
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Heslo sa nepodarilo zmeniť.');
      })
      .finally(() => setPrebieha(false));
  };

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Zmena hesla</SectionTitle>
      <p className="text-sm text-(--color-text-muted)">
        Po zmene sa odhlásia všetky ostatné prihlásenia tohto účtu. Toto okno zostane prihlásené.
      </p>

      <form onSubmit={odosli} className="flex flex-col gap-4">
        <Field
          label="Súčasné heslo"
          type="password"
          value={sucasne}
          onChange={setSucasne}
          required
          autoComplete="current-password"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Nové heslo"
            type="password"
            value={nove}
            onChange={setNove}
            required
            autoComplete="new-password"
            hint="Aspoň 12 znakov. Dlhšie heslo je lepšie než zložitejšie."
          />
          <Field
            label="Nové heslo znova"
            type="password"
            value={znova}
            onChange={setZnova}
            required
            autoComplete="new-password"
          />
        </div>

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
        {hotovo && <Notice tone="ok">Heslo je zmenené.</Notice>}

        <div>
          <Button type="submit" variant="primary" disabled={prebieha}>
            {prebieha ? 'Mením…' : 'Zmeniť heslo'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function FormularUctu({
  poZalozeni,
  zavri,
}: {
  readonly poZalozeni: () => void;
  readonly zavri: () => void;
}): ReactNode {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);

  const odosli = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    setPrebieha(true);
    void account
      .createUser(username, password, role)
      .then(() => {
        poZalozeni();
        zavri();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Účet sa nepodarilo založiť.');
      })
      .finally(() => setPrebieha(false));
  };

  return (
    <form onSubmit={odosli} className="flex flex-col gap-4 border-t border-(--color-border) pt-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Prihlasovacie meno"
          value={username}
          onChange={setUsername}
          required
          autoComplete="off"
        />
        <Field
          label="Heslo"
          type="password"
          value={password}
          onChange={setPassword}
          required
          autoComplete="new-password"
          hint="Aspoň 12 znakov."
        />
        <label className="flex flex-col gap-1.5 text-sm font-medium text-(--color-text)">
          Rola
          <select
            className="w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text)"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            <option value="viewer">Len čítanie</option>
            <option value="admin">Správca</option>
          </select>
          <span className="text-xs font-normal text-(--color-text-subtle)">
            Správca môže meniť všetko vrátane párovania Miniservera a lokálnych kľúčov.
          </span>
        </label>
      </div>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={prebieha}>
          {prebieha ? 'Zakladám…' : 'Založiť účet'}
        </Button>
        <Button variant="ghost" onClick={zavri} disabled={prebieha}>
          Zrušiť
        </Button>
      </div>
    </form>
  );
}

function RiadokUctu({
  ucet,
  jaId,
  poZmene,
}: {
  readonly ucet: UcetPrehlad;
  readonly jaId: string | undefined;
  readonly poZmene: () => void;
}): ReactNode {
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [odobera, setOdobera] = useState(false);
  // Zmena roly sa pyta rovnako ako odobranie uctu: je to zmena
  // opravneni, nie prepinac vzhladu.
  const [menirolu, setMeniRolu] = useState(false);
  const jaSam = ucet.id === jaId;

  const zmenRolu = (role: Role) => {
    setChyba(undefined);
    void account
      .setRole(ucet.id, role)
      .then(poZmene)
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Rolu sa nepodarilo zmeniť.');
      });
  };

  const odober = () => {
    setChyba(undefined);
    void account
      .removeUser(ucet.id)
      .then(() => {
        setOdobera(false);
        poZmene();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Účet sa nepodarilo odobrať.');
      });
  };

  return (
    <li className="flex flex-col gap-3 border-b border-(--color-border) py-3 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-(--color-text)">
            {ucet.username}
            {jaSam && <span className="text-(--color-text-subtle)"> — to si ty</span>}
          </span>
          <span className="text-xs text-(--color-text-subtle)">
            {ucet.lastLoginAt === null
              ? 'zatiaľ sa neprihlásil'
              : `naposledy ${new Date(ucet.lastLoginAt).toLocaleString('sk-SK')}`}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={ucet.role === 'admin' ? 'accent' : 'neutral'}>
            {ucet.role === 'admin' ? 'Správca' : 'Len čítanie'}
          </Badge>
          {menirolu ? (
            <>
              <Button
                variant="danger"
                onClick={() => {
                  setMeniRolu(false);
                  zmenRolu(ucet.role === 'admin' ? 'viewer' : 'admin');
                }}
              >
                {ucet.role === 'admin' ? 'Naozaj zmeniť na čítanie' : 'Naozaj urobiť správcom'}
              </Button>
              <Button variant="ghost" onClick={() => setMeniRolu(false)}>
                Nechať
              </Button>
            </>
          ) : (
            <Button onClick={() => setMeniRolu(true)}>
              {ucet.role === 'admin' ? 'Zmeniť na čítanie' : 'Urobiť správcom'}
            </Button>
          )}
          {!jaSam &&
            (odobera ? (
              <>
                <Button variant="danger" onClick={odober}>
                  Naozaj odobrať
                </Button>
                <Button variant="ghost" onClick={() => setOdobera(false)}>
                  Nechať
                </Button>
              </>
            ) : (
              <Button
                variant="danger"
                icon={<IconTrash className="size-4" />}
                onClick={() => setOdobera(true)}
              >
                Odobrať
              </Button>
            ))}
        </div>
      </div>

      {menirolu && (
        <Notice tone="warn">
          {ucet.role === 'admin' ? (
            <>
              <p className="font-medium">Zobrať účtu {ucet.username} práva správcu?</p>
              <p className="mt-1">
                Prestane môcť čokoľvek meniť — ovládať zariadenia, upravovať mapovania ani sťahovať
                šablóny. Čítať bude môcť ďalej. Platí to okamžite, aj keď je práve prihlásený.
              </p>
            </>
          ) : (
            <>
              <p className="font-medium">Dať účtu {ucet.username} práva správcu?</p>
              <p className="mt-1">
                Správca môže zobraziť lokálne kľúče zariadení Tuya, obnoviť bránu zo zálohy, prepnúť
                USB rádio aj aktualizovať celú bránu. Je to plný prístup, nie o jeden stupeň viac.
              </p>
            </>
          )}
        </Notice>
      )}

      {odobera && (
        <Notice tone="warn">
          <p className="font-medium">Odobrať účet {ucet.username}?</p>
          <p className="mt-1">
            Prihlásenie z tohto účtu skončí okamžite — všetky jeho relácie sa zmažú. Zariadenia,
            mapovania ani zálohy sa nedotkne; účet sa dá kedykoľvek založiť znova, ale s novým
            heslom.
          </p>
        </Notice>
      )}

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
    </li>
  );
}

/** Zoznam uctov. Zobrazuje sa len spravcovi; rozhoduje o tom server. */
export function SpravaUctov(): ReactNode {
  const { user } = useSession();
  const [zaklada, setZaklada] = useState(false);
  const nacitanie = useApi((signal) => account.users(signal));

  if (user?.role !== 'admin') return null;

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Účty</SectionTitle>
        <Button
          icon={<IconPlus className="size-4" />}
          onClick={() => setZaklada(true)}
          disabled={zaklada}
        >
          Nový účet
        </Button>
      </div>

      <Data nacitanie={nacitanie}>
        {(data) => (
          <ul className="flex flex-col">
            {data.users.map((u) => (
              <RiadokUctu key={u.id} ucet={u} jaId={user.id} poZmene={nacitanie.reload} />
            ))}
          </ul>
        )}
      </Data>

      {zaklada && <FormularUctu poZalozeni={nacitanie.reload} zavri={() => setZaklada(false)} />}
    </Card>
  );
}
